import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FileQuestion } from 'lucide-react';
import { I18nProvider, useI18n } from './i18n';
import { ThemeProvider } from './hooks/useTheme';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './components/ui/Toast';
import { TooltipProvider } from './components/ui/primitives';
import { AppShell } from './components/AppShell';
import { ChangePassword, Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Articles } from './pages/Articles';
import { Categories } from './pages/Categories';
import { Leads } from './pages/Leads';
import { Media } from './pages/Media';
import { Pages } from './pages/Pages';
import { Redirects } from './pages/Redirects';
import { Services } from './pages/Services';
import { Stories } from './pages/Stories';
import { Settings } from './pages/Settings';
import { EmptyState, Skeleton } from './components/ui/Feedback';
import { Button } from './components/ui/Button';

/*
 * TipTap and Framer Motion are the two heavy dependencies. Every screen that
 * pulls them is lazy, so the list screens — where a marketer spends most of
 * the day — stay inside the 200 KB initial-JS budget (§10.9).
 */
const ArticleEditor = lazy(() =>
  import('./pages/ArticleEditor').then((module) => ({ default: module.ArticleEditor })),
);
const ServiceEditor = lazy(() =>
  import('./pages/ServiceEditor').then((module) => ({ default: module.ServiceEditor })),
);
const PageEditor = lazy(() =>
  import('./pages/PageEditor').then((module) => ({ default: module.PageEditor })),
);
// MapLibre is ~800 KB. It must never land in the initial bundle for the
// marketers who will never open this screen.
const Scans = lazy(() => import('./pages/Scans').then((module) => ({ default: module.Scans })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401 is handled by the axios interceptor; retrying it just delays the
      // redirect to the login screen.
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <TooltipProvider delayDuration={350}>
            <ToastProvider>
              <AuthProvider>
                <BrowserRouter>
                  <Gate />
                </BrowserRouter>
              </AuthProvider>
            </ToastProvider>
          </TooltipProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

function Gate() {
  const { status, user, can } = useAuth();

  if (status === 'checking') return <BootSkeleton />;
  if (status === 'anonymous') return <Login />;

  // A forced password change blocks every other navigation until it is done
  // (§4.2). The server enforces it too; this is what makes it not feel like
  // a wall of 403s.
  if (user?.mustChangePassword) return <ChangePassword />;

  // Each route is wrapped in the SAME page permission the server gates it with.
  // A user who types a URL they lack is sent home rather than shown a wall of
  // 403s — the sidebar never offers the link in the first place.
  const guard = (page: Parameters<typeof can>[0], element: React.ReactNode) =>
    can(page) ? element : <Navigate to="/" replace />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Overview />} />
        <Route path="articles" element={guard('articles', <Articles />)} />
        <Route path="articles/:id" element={guard('articles', <Lazy><ArticleEditor /></Lazy>)} />
        <Route path="services" element={guard('services', <Services />)} />
        <Route path="services/:id" element={guard('services', <Lazy><ServiceEditor /></Lazy>)} />
        {/* The ring editor opens as a modal over the tray — no separate route. */}
        <Route path="stories" element={guard('stories', <Stories />)} />
        <Route path="pages" element={guard('pages', <Pages />)} />
        <Route path="pages/:route" element={guard('pages', <Lazy><PageEditor /></Lazy>)} />
        <Route path="media" element={guard('media', <Media />)} />
        <Route path="categories" element={guard('categories', <Categories />)} />
        <Route path="redirects" element={guard('redirects', <Redirects />)} />
        <Route path="leads" element={guard('leads', <Leads />)} />
        {/* User & role management is gated on the 'users' page. */}
        <Route path="settings" element={guard('users', <Settings />)} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* RS telemetriya konsoli AppShell'dan TASHQARIDA: u CMS bo'limi emas,
          omborni nazorat qiladigan alohida ekran — o'z to'liq ekranli qobig'i,
          o'z palitrasi. Skan ma'lumoti xodim joylashuvini saqlaydi, shuning
          uchun 'scans' ruxsati talab qilinadi. */}
      <Route
        path="/scans"
        element={
          can('scans') ? (
            <Suspense fallback={<ConsoleFallback />}>
              <Scans />
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/login" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Skeleton, not a spinner, while a route chunk loads (§10.6). */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<EditorFallback />}>{children}</Suspense>;
}

function EditorFallback() {
  return (
    <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-6 py-8 lg:grid-cols-[minmax(0,720px)_360px]">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-9 w-full rounded-control" />
        <Skeleton className="h-80 w-full rounded-card" />
      </div>
      <div className="flex flex-col gap-6">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-40 w-full rounded-card" />
      </div>
    </div>
  );
}

/**
 * The console is a dark surface; the CMS editor skeleton would flash a light
 * panel in the middle of it while MapLibre's chunk loads.
 */
function ConsoleFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#080B10]">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1E2735] border-t-[#5B8DEF]" />
    </div>
  );
}

function BootSkeleton() {
  return (
    <div className="flex min-h-screen bg-canvas">
      <div className="hidden w-sidebar border-r border-line bg-surface p-4 lg:block">
        <Skeleton className="mb-6 h-8 w-32 rounded-control" />
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="mb-2 h-9 w-full rounded-control" />
        ))}
      </div>
      <div className="flex-1 p-6">
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="h-80 w-full rounded-card" />
      </div>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="mx-auto max-w-content px-6 py-16">
      <EmptyState
        icon={<FileQuestion className="h-6 w-6" />}
        title={t.errors.notFound}
        hint={t.errors.notFoundHint}
        action={
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            {t.errors.goHome}
          </Button>
        }
      />
    </div>
  );
}
